export type YearReviewData = {
  year:               number
  periodLabel:        string
  totalItemsTracked:  number
  totalConfirmations: number
  totalWastedDollars: number
  totalSavedDollars:  number
  totalSpent:         number
  bestMonth:          string | null
  worstMonth:         string | null
  mostReliableItem: {
    name:   string
    emoji:  string
    streak: number
  } | null
  mostWastedItem: {
    name:          string
    emoji:         string
    wastedDollars: number
  } | null
  totalShops:          number
  avgItemsPerShop:     number
  topCategories: {
    category: string
    count:    number
    spent:    number
  }[]
  monthlySpend: {
    month: string
    spent: number
  }[]
  wasteReduced:          boolean
  personalInflationRate: number | null
}

export async function calculateYearReview(
  householdId: string,
  supabase: any,
  year: number,
): Promise<YearReviewData> {
  const startDate = new Date(year, 0, 1)
  const endDate   = new Date(year, 11, 31, 23, 59, 59)
  const startStr  = startDate.toISOString()
  const endStr    = endDate.toISOString()

  // Fetch all confirmations for the year
  const { data: confirmations } = await supabase
    .from('confirmations')
    .select(`
      id, item_id, confirmed_at,
      price_paid, store_name, quantity_bought,
      pantry_items(name, emoji, display_category)
    `)
    .eq('household_id', householdId)
    .gte('confirmed_at', startStr)
    .lte('confirmed_at', endStr)
    .order('confirmed_at', { ascending: true })

  // Fetch expiration events for the year
  const { data: expirationEvents } = await supabase
    .from('expiration_events')
    .select('*')
    .eq('household_id', householdId)
    .gte('confirmed_at', startStr)
    .lte('confirmed_at', endStr)
    .not('confirmed_at', 'is', null)

  // Fetch shopping lists for the year
  const { data: shoppingLists } = await supabase
    .from('shopping_lists')
    .select('id, generated_at, completed_at')
    .eq('household_id', householdId)
    .gte('generated_at', startStr)
    .lte('generated_at', endStr)
    .not('completed_at', 'is', null)

  // Fetch pantry items
  const { data: pantryItems } = await supabase
    .from('pantry_items')
    .select('id, name, emoji, display_category, total_wasted_dollars, confirmation_count')
    .eq('household_id', householdId)

  // ── Totals ──────────────────────────────────────────────────────────────────

  const totalConfirmations = confirmations?.length || 0
  const totalItemsTracked  = new Set(confirmations?.map((c: any) => c.item_id)).size

  const totalSpent = confirmations?.reduce(
    (sum: number, c: any) => sum + (c.price_paid || 0), 0,
  ) || 0

  const totalWastedDollars = expirationEvents
    ?.filter((e: any) => e.used_before_expiry === false)
    .reduce((sum: number, e: any) =>
      sum + (e.wasted_dollars || e.price_paid || 0), 0,
    ) || 0

  const totalSavedDollars = expirationEvents
    ?.filter((e: any) => e.used_before_expiry === true)
    .reduce((sum: number, e: any) => sum + (e.price_paid || 0), 0) || 0

  // ── Monthly spend ────────────────────────────────────────────────────────────

  const monthlySpendMap: Record<string, number> = {}
  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
    'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

  for (const conf of confirmations || []) {
    if (!conf.price_paid) continue
    const month = monthNames[new Date(conf.confirmed_at).getMonth()]
    monthlySpendMap[month] = (monthlySpendMap[month] || 0) + conf.price_paid
  }

  const monthlySpend = monthNames
    .filter(m => monthlySpendMap[m] !== undefined)
    .map(m => ({ month: m, spent: monthlySpendMap[m] }))

  // ── Best and worst months ────────────────────────────────────────────────────

  const sortedMonths = [...monthlySpend].sort((a, b) => a.spent - b.spent)
  const bestMonth    = sortedMonths[0]?.month || null
  const worstMonth   = sortedMonths[sortedMonths.length - 1]?.month || null

  // ── Top categories ───────────────────────────────────────────────────────────

  const categoryMap: Record<string, { count: number; spent: number }> = {}
  for (const conf of confirmations || []) {
    const cat = (conf.pantry_items as any)?.display_category || 'other'
    if (!categoryMap[cat]) categoryMap[cat] = { count: 0, spent: 0 }
    categoryMap[cat].count++
    categoryMap[cat].spent += conf.price_paid || 0
  }

  const topCategories = Object.entries(categoryMap)
    .map(([category, data]) => ({ category, count: data.count, spent: data.spent }))
    .sort((a, b) => b.count - a.count)
    .slice(0, 4)

  // ── Most reliable item ───────────────────────────────────────────────────────

  const itemConfirmationCount: Record<string, number> = {}
  for (const conf of confirmations || []) {
    itemConfirmationCount[conf.item_id] = (itemConfirmationCount[conf.item_id] || 0) + 1
  }

  const wastedItemIds = new Set(
    expirationEvents
      ?.filter((e: any) => e.used_before_expiry === false)
      .map((e: any) => e.item_id),
  )

  const mostReliableItemId = Object.entries(itemConfirmationCount)
    .filter(([id]) => !wastedItemIds.has(id))
    .sort(([, a], [, b]) => b - a)[0]?.[0]

  const mostReliableItemData = pantryItems?.find((i: any) => i.id === mostReliableItemId)

  const mostReliableItem = mostReliableItemData
    ? {
        name:   mostReliableItemData.name,
        emoji:  mostReliableItemData.emoji || '📦',
        streak: itemConfirmationCount[mostReliableItemId!] || 0,
      }
    : null

  // ── Most wasted item ─────────────────────────────────────────────────────────

  const wasteByItem: Record<string, number> = {}
  for (const event of expirationEvents || []) {
    if (event.used_before_expiry === false) {
      wasteByItem[event.item_id] =
        (wasteByItem[event.item_id] || 0) + (event.wasted_dollars || event.price_paid || 0)
    }
  }

  const mostWastedItemId   = Object.entries(wasteByItem).sort(([, a], [, b]) => b - a)[0]?.[0]
  const mostWastedItemData = pantryItems?.find((i: any) => i.id === mostWastedItemId)

  const mostWastedItem = mostWastedItemData
    ? {
        name:          mostWastedItemData.name,
        emoji:         mostWastedItemData.emoji || '📦',
        wastedDollars: wasteByItem[mostWastedItemId!] || 0,
      }
    : null

  // ── Shopping stats ───────────────────────────────────────────────────────────

  const totalShops      = shoppingLists?.length || 0
  const avgItemsPerShop = totalShops > 0 ? Math.round(totalConfirmations / totalShops) : 0

  const wasteReduced = totalSavedDollars > totalWastedDollars

  const isCurrentYear = year === new Date().getFullYear()
  const periodLabel   = isCurrentYear ? 'Last 12 months' : `${year}`

  return {
    year,
    periodLabel,
    totalItemsTracked,
    totalConfirmations,
    totalWastedDollars,
    totalSavedDollars,
    totalSpent,
    bestMonth,
    worstMonth,
    mostReliableItem,
    mostWastedItem,
    totalShops,
    avgItemsPerShop,
    topCategories,
    monthlySpend,
    wasteReduced,
    personalInflationRate: null,
  }
}
